// src/features/results/ContentSection/Panels/EmotionAnalysisPanel/index.tsx
import React, { useEffect, useMemo, useRef } from "react";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  Tooltip,
  type ChartConfiguration,
} from "chart.js";
import * as S from "./EmotionAnalysisPanel.styles";
import emotionChart from "../../../../../assets/voice_understand/emotion_chart.svg";
import { PanelContainer } from "../ConversationPanel/ConversationPanel.styles";
import { ContentContainer } from "../AudioAnalysisPanel/AudioAnalysisPanel.styles";
import { CommonActionBar } from "../../components/ActionBar";
import { ActionType, DEFAULT_ACTION_STYLES } from "../../components/ActionBar";
import { SpeakerEmotionBadge } from "../../../../../components/SpeakerEmotionBadge";
// import { useProcessVuStore } from "../../../../../store/processVuStore"; // 삭제
import { formatTime } from "../../../../../utils/formatTime";
import { ProcessResult as VUResult, Utterance } from "types/output_vu";

// Register the controllers and elements
Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  Tooltip
);

export interface EmotionStat {
  label: string;
  count: number;
}

export interface KeySentence {
  speaker: string;
  emotion: string;
  text: string;
  timeRange: string;
}

interface EmotionAnalysisPanelProps {
  result: VUResult | null;
  onCTAClick: () => void;
  onCopy: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDownload: () => void;
}

// 감정 한글 변환 함수
const convertEmotionToKorean = (emotion: string): string => {
  const emotionMap: { [key: string]: string } = {
    NEUTRAL: "중립",
    HAPPINESS: "기쁨",
    ANGRY: "화남",
    SADNESS: "슬픔",
    SURPRISE: "놀람",
  };
  return emotionMap[emotion] || "중립";
};

const EmotionAnalysisPanel: React.FC<EmotionAnalysisPanelProps> = ({
  result,
  onCTAClick,
  onCopy,
  onShare,
  onEdit,
  onDownload,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const emotionData = useMemo(() => {
    if (!result) {
      return {
        emotionalRatio: 0,
        emotionalSentences: 0,
        totalSentences: 0,
        stats: [],
        keySentences: [],
      };
    }

    // getEmotionTimeline 로직을 useMemo 내부로 이동
    const timeline = (result.output.utterances || [])
      .map((u: Utterance) => {
        if (!u.emotion_info) return null; // emotion_info가 없는 경우 제외
        return {
          time: u.start,
          speaker: u.speaker,
          emotion: u.emotion,
          intensity: Math.max(
            u.emotion_info.emotion.ANGRY,
            u.emotion_info.emotion.HAPPINESS,
            u.emotion_info.emotion.NEUTRAL,
            u.emotion_info.emotion.SADNESS,
            u.emotion_info.emotion.SURPRISE
          ),
          text: u.text,
          start: u.start,
          end: u.end,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const totalSentences = result.output.utterances.length;
    const specifiedEmotions = ["HAPPINESS", "SADNESS", "ANGRY", "SURPRISE"];

    const emotionalTimeline = timeline.filter((e) =>
      specifiedEmotions.includes(e.emotion)
    );
    const emotionalSentences = emotionalTimeline.length;

    const emotionalRatio =
      totalSentences > 0
        ? Math.round((emotionalSentences / totalSentences) * 100)
        : 0;

    const baseEmotions: Record<string, number> = {
      HAPPINESS: 0,
      SADNESS: 0,
      ANGRY: 0,
      SURPRISE: 0,
    };

    const emotionCounts = emotionalTimeline.reduce(
      (acc: Record<string, number>, curr) => {
        acc[curr.emotion] = (acc[curr.emotion] || 0) + 1;
        return acc;
      },
      baseEmotions
    );

    const stats: EmotionStat[] = Object.entries(emotionCounts).map(
      ([emotion, count]) => ({
        label: convertEmotionToKorean(emotion),
        count,
      })
    );

    const keySentences = emotionalTimeline
      .sort((a, b) => b.intensity - a.intensity)
      .slice(0, 5)
      .map((t) => {
        const speakerId = t.speaker
          .replace("SPEAKER-", "")
          .replace("SPEAKER_", "");
        return {
          speaker: String.fromCharCode(65 + parseInt(speakerId, 10)),
          emotion: convertEmotionToKorean(t.emotion),
          text: t.text,
          timeRange: `${formatTime(t.start)} - ${formatTime(t.end)}`,
        };
      });

    return {
      emotionalRatio,
      emotionalSentences,
      totalSentences,
      stats,
      keySentences,
    };
  }, [result]);

  const {
    emotionalRatio,
    emotionalSentences,
    totalSentences,
    stats,
    keySentences,
  } = emotionData;

  const overviewText = useMemo(() => {
    if (result?.output.mllm?.emotion) {
      return result.output.mllm.emotion;
    }
    return `전체 ${totalSentences}개의 문장 중 ${emotionalSentences}개 (${emotionalRatio}%)의 문장에서 감정이 드러났어요.`;
  }, [result, totalSentences, emotionalSentences, emotionalRatio]);

  useEffect(() => {
    if (!canvasRef.current || stats.length === 0) return;

    const labels = stats.map((s) => s.label);
    const data = stats.map((s) => s.count);

    const config: ChartConfiguration<"radar"> = {
      type: "radar" as const,
      data: {
        labels,
        datasets: [
          {
            label: "감정데이터",
            data,
            fill: true,
            backgroundColor: "rgba(225, 234, 254, 0.5)",
            borderColor: "#467FF2",
            pointBackgroundColor: "#467FF2",
          },
        ],
      },
      options: {
        elements: { line: { borderWidth: 1 } },
        scales: {
          r: {
            min: 0,
            grid: { display: false },
            angleLines: { display: false },
            ticks: { display: false },
            pointLabels: {
              font: {
                family: "Pretendard",
                size: 12,
                weight: 400,
              },
              color: "#727272",
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        maintainAspectRatio: false,
      },
    };

    const ctx = canvasRef.current.getContext("2d")!;
    const chartInstance = new Chart(ctx, config);

    return () => {
      chartInstance.destroy();
    };
  }, [stats]);

  // const leftActions = [
  //   {
  //     type: "copy" as ActionType,
  //     label: "복사",
  //     onClick: onCopy,
  //     ...DEFAULT_ACTION_STYLES.copy,
  //   },
  //   {
  //     type: "share" as ActionType,
  //     label: "공유",
  //     onClick: onShare,
  //     ...DEFAULT_ACTION_STYLES.share,
  //   },
  //   {
  //     type: "edit" as ActionType,
  //     label: "수정",
  //     onClick: onEdit,
  //     ...DEFAULT_ACTION_STYLES.edit,
  //   },
  // ];

  const rightActions = [
    {
      type: "download" as ActionType,
      label: "다운로드",
      onClick: onDownload,
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  return (
    <PanelContainer>
      {/* 상단: Overview + Stats + Radar */}
      <ContentContainer>
        <S.TopSection>
          <S.LeftSection>
            <h2>Overview</h2>
            <S.OverviewText>{overviewText}</S.OverviewText>
            <S.Ratio>
              {emotionalRatio}%<br />
              <small>전체 문장 중 감정적인 문장</small>
            </S.Ratio>
            <S.StatsTable>
              {stats.map((s, i) => (
                <S.StatCell key={i} isFirst={i === 0}>
                  <span className="label">{s.label}</span>
                  <span className="count">{s.count}</span>
                </S.StatCell>
              ))}
            </S.StatsTable>
          </S.LeftSection>
          <S.RadarWrapper backgroundImage={emotionChart}>
            <canvas ref={canvasRef} />
          </S.RadarWrapper>
        </S.TopSection>
        {/* 핵심 감정 문장 */}
        <S.KeySentencesSection>
          <h3>핵심 감정 문장</h3>
          {keySentences.map((ks, i) => (
            <S.KeySentenceItem key={i}>
              <SpeakerEmotionBadge
                speaker={`화자 ${ks.speaker}`}
                emotion={ks.emotion}
                showIcon={true}
              />
              <span className="text">{ks.text}</span>
              <span className="time">{ks.timeRange}</span>
            </S.KeySentenceItem>
          ))}
        </S.KeySentencesSection>
      </ContentContainer>

      {/* CTA 배너 */}
      <S.CTABanner onClick={onCTAClick}>
        보이스하이라이트에서 더 자세하게 분석해보세요 →
      </S.CTABanner>

      {/* 액션 버튼 */}
      <CommonActionBar leftActions={[]} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default EmotionAnalysisPanel;
